import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';

import { Building, BuildingCategory, BuildMenuState } from '../constants';
import { setTickSystemPaused } from '../hooks/useGameTick';
import { createToggleUICommand } from '../InputCommands';
import { InputCommandType } from '../InputCommands';
import { InputManager } from '../InputManager';

// UI State interface
interface UIState {
  // Menu states
  saveMenuOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  helpMenuOpen: boolean;
  leftPanelOpen: boolean;
  inventoryOpen: boolean;
  inventoryPulseId: number;
  buildMenuOpen: BuildMenuState; // Main build menu (B key)
  researchMenuOpen: boolean;
  prestigeMenuOpen: boolean;
  productionMenuOpen: boolean;
  startMenuOpen: boolean;

  // Building state
  buildMode: boolean; // Derived from selectedBuilding !== null
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;

  // Building status menu target (now managed by GameEngine)
  buildingStatusMenuTarget: { x: number; y: number } | null;
}

// UI Actions interface
interface UIActions {
  // Menu actions
  setSaveMenuOpen: (open: boolean) => void;
  setDebugMenuOpen: (open: boolean) => void;
  setAchievementMenuOpen: (open: boolean) => void;
  setHelpMenuOpen: (open: boolean) => void;
  setLeftPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setInventoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerInventoryPulse: () => void;
  setBuildMenuOpen: (open: boolean) => void; // Main build menu (B key)
  setResearchMenuOpen: (open: boolean) => void;
  setPrestigeMenuOpen: (open: boolean) => void;
  setProductionMenuOpen: (open: boolean) => void;
  setStartMenuOpen: (open: boolean) => void;

  // Building actions
  setSelectedCategory: (category: BuildingCategory | null) => void;
  setSelectedBuilding: (building: Building | null) => void;
  setBuildMenuState: (updates: {
    buildMenuOpen?: BuildMenuState;
    selectedCategory?: BuildingCategory | null;
    selectedBuilding?: Building | null;
  }) => void;
  setBuildingStatusMenuTarget: (
    target: { x: number; y: number } | null
  ) => void;

  // Combined actions
  exitBuildMode: () => void;
  openBuildingStatusMenu: (position: { x: number; y: number }) => void;
  closeBuildingStatusMenu: () => void;
}

// Combined context type
type UIContextType = UIState & UIActions;

// Create context
const UIContext = createContext<UIContextType | undefined>(undefined);

// Hook to use UI context
export const useUIState = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUIState must be used within UIStateManager');
  }
  return context;
};

// UIStateManager component
interface UIStateManagerProps {
  children: ReactNode;
  buildingState?: {
    buildMode: boolean; // Derived from GameEngine selectedBuilding !== null
    selectedCategory: BuildingCategory | null;
    selectedBuilding: Building | null;
    buildMenuOpen: BuildMenuState; // Main build menu (B key)
    setSelectedCategory: (category: BuildingCategory | null) => void;
    setSelectedBuilding: (building: Building | null) => void;
    setBuildMenuOpen: (open: boolean) => void; // Main build menu (B key)
    setBuildMenuState: (updates: {
      buildMenuOpen?: BuildMenuState;
      selectedCategory?: BuildingCategory | null;
      selectedBuilding?: Building | null;
    }) => void;
    exitBuildMode: () => void;
  };
  // UI menu states from game engine (read-only)
  gameEngineUIState?: {
    helpMenuOpen: boolean;
    saveMenuOpen: boolean;
    leftPanelOpen: boolean;
    debugMenuOpen: boolean;
    achievementMenuOpen: boolean;
    researchMenuOpen: boolean;
    prestigeMenuOpen: boolean;
    productionMenuOpen: boolean;
    buildingStatusMenuTarget: { x: number; y: number } | null;
  };
  // GameEngine actions for building status menu
  gameEngineActions?: {
    openBuildingStatusMenu: (position: { x: number; y: number }) => void;
    closeBuildingStatusMenu: () => void;
    setBuildingStatusMenuTarget: (
      target: { x: number; y: number } | null
    ) => void;
    setResearchMenuOpen: (open: boolean) => void;
    setPrestigeMenuOpen: (open: boolean) => void;
    setProductionMenuOpen: (open: boolean) => void;
  };
}

export const UIStateManager: React.FC<UIStateManagerProps> = ({
  children,
  buildingState,
  gameEngineUIState,
  gameEngineActions,
}) => {
  // Menu states - use game engine state if available, otherwise use local state
  const [localSaveMenuOpen, setLocalSaveMenuOpen] = useState<boolean>(false);
  const [localDebugMenuOpen, setLocalDebugMenuOpen] = useState<boolean>(false);
  const [localHelpMenuOpen, setLocalHelpMenuOpen] = useState<boolean>(false);
  const [localLeftPanelOpen, setLocalLeftPanelOpen] = useState<boolean>(false);
  const [localResearchMenuOpen, setLocalResearchMenuOpen] =
    useState<boolean>(false);
  const [localProductionMenuOpen, setLocalProductionMenuOpen] =
    useState<boolean>(false);
  const [localPrestigeMenuOpen, setLocalPrestigeMenuOpen] =
    useState<boolean>(false);
  const [startMenuOpen, setStartMenuOpenState] = useState<boolean>(true);
  const [inventoryOpen, setInventoryOpenState] = useState<boolean>(false);
  const [inventoryPulseId, setInventoryPulseId] = useState<number>(0);

  // Use game engine state if available (read-only), otherwise use local state
  const saveMenuOpen = gameEngineUIState?.saveMenuOpen ?? localSaveMenuOpen;
  const debugMenuOpen = gameEngineUIState?.debugMenuOpen ?? localDebugMenuOpen;
  const achievementMenuOpen = gameEngineUIState?.achievementMenuOpen ?? false;
  const helpMenuOpen = gameEngineUIState?.helpMenuOpen ?? localHelpMenuOpen;
  const leftPanelOpen = gameEngineUIState?.leftPanelOpen ?? localLeftPanelOpen;
  const researchMenuOpen =
    gameEngineUIState?.researchMenuOpen ?? localResearchMenuOpen;
  const productionMenuOpen =
    gameEngineUIState?.productionMenuOpen ?? localProductionMenuOpen;
  const prestigeMenuOpen =
    gameEngineUIState?.prestigeMenuOpen ?? localPrestigeMenuOpen;
  const buildingStatusMenuTarget =
    gameEngineUIState?.buildingStatusMenuTarget ?? null;
  const effectiveStartMenuOpen = startMenuOpen;

  useEffect(() => {
    setTickSystemPaused(effectiveStartMenuOpen);

    return () => {
      setTickSystemPaused(false);
    };
  }, [effectiveStartMenuOpen]);

  // Setters - dispatch commands if using game engine state, otherwise use local state
  const setSaveMenuOpen = gameEngineUIState
    ? (open: boolean) => {
        if (saveMenuOpen === open) {
          return;
        }

        InputManager.getInstance().triggerCommand(
          createToggleUICommand(InputCommandType.TOGGLE_SAVE_MENU)
        );
      }
    : setLocalSaveMenuOpen;
  const setDebugMenuOpen = gameEngineUIState
    ? () =>
        InputManager.getInstance().triggerCommand(
          createToggleUICommand(InputCommandType.TOGGLE_DEBUG_MENU)
        )
    : setLocalDebugMenuOpen;
  const setAchievementMenuOpen = gameEngineUIState
    ? () =>
        InputManager.getInstance().triggerCommand(
          createToggleUICommand(InputCommandType.TOGGLE_ACHIEVEMENT_MENU)
        )
    : () => {}; // No local state for achievement menu
  const setHelpMenuOpen = gameEngineUIState
    ? () =>
        InputManager.getInstance().triggerCommand(
          createToggleUICommand(InputCommandType.TOGGLE_HELP_MENU)
        )
    : setLocalHelpMenuOpen;
  const setLeftPanelOpen = gameEngineUIState
    ? () =>
        InputManager.getInstance().triggerCommand(
          createToggleUICommand(InputCommandType.TOGGLE_LEFT_PANEL)
        )
    : setLocalLeftPanelOpen;
  const setResearchMenuOpen: (open: boolean) => void = open => {
    if (gameEngineActions?.setResearchMenuOpen) {
      gameEngineActions.setResearchMenuOpen(open);
    } else {
      setLocalResearchMenuOpen(open);
    }
  };

  const setProductionMenuOpen = (open: boolean): void => {
    if (gameEngineActions?.setProductionMenuOpen) {
      gameEngineActions.setProductionMenuOpen(open);
    } else {
      setLocalProductionMenuOpen(open);
    }
  };

  const setPrestigeMenuOpen = (open: boolean): void => {
    if (gameEngineActions?.setPrestigeMenuOpen) {
      gameEngineActions.setPrestigeMenuOpen(open);
    } else {
      setLocalPrestigeMenuOpen(open);
    }
  };

  const setStartMenuOpen = (open: boolean): void => {
    setStartMenuOpenState(open);
  };

  const [localBuildMenuOpen, setLocalBuildMenuOpen] =
    useState<BuildMenuState>(false);

  // Building states - use passed buildingState or defaults
  const [localSelectedCategory, setLocalSelectedCategory] =
    useState<BuildingCategory | null>(null);
  const [localSelectedBuilding, setLocalSelectedBuilding] =
    useState<Building | null>(null);

  // Build mode is now derived from GameEngine state passed via buildingState
  const buildMode = buildingState?.buildMode ?? localSelectedBuilding !== null;
  const selectedCategory =
    buildingState?.selectedCategory ?? localSelectedCategory;
  const selectedBuilding =
    buildingState?.selectedBuilding ?? localSelectedBuilding;
  const buildMenuOpen = buildingState?.buildMenuOpen ?? localBuildMenuOpen;
  const setSelectedCategory =
    buildingState?.setSelectedCategory ?? setLocalSelectedCategory;
  const setSelectedBuilding =
    buildingState?.setSelectedBuilding ?? setLocalSelectedBuilding;
  const setBuildMenuOpen =
    buildingState?.setBuildMenuOpen ?? setLocalBuildMenuOpen;

  // Combined actions
  const exitBuildMode = () => {
    if (buildingState?.exitBuildMode) {
      buildingState.exitBuildMode();
    } else {
      setSelectedBuilding(null);
      setSelectedBuilding(null);
      setSelectedCategory(null);
    }
  };

  const openBuildingStatusMenu = (position: { x: number; y: number }) => {
    if (gameEngineActions?.openBuildingStatusMenu) {
      gameEngineActions.openBuildingStatusMenu(position);
    }
    // No fallback needed - if GameEngine actions aren't available, the UI won't show the menu
  };

  const closeBuildingStatusMenu = () => {
    if (gameEngineActions?.closeBuildingStatusMenu) {
      gameEngineActions.closeBuildingStatusMenu();
    }
    // No fallback needed - if GameEngine actions aren't available, the UI won't show the menu
  };

  const setBuildingStatusMenuTarget = (
    target: { x: number; y: number } | null
  ) => {
    if (gameEngineActions?.setBuildingStatusMenuTarget) {
      gameEngineActions.setBuildingStatusMenuTarget(target);
    }
    // No fallback needed since this was local state before
  };

  // Unified build menu state setter - delegates to buildingState if available
  const setBuildMenuState = (updates: {
    buildMenuOpen?: BuildMenuState;
    selectedCategory?: BuildingCategory | null;
    selectedBuilding?: Building | null;
  }) => {
    if (buildingState?.setBuildMenuState) {
      buildingState.setBuildMenuState(updates);
    } else {
      // Fallback to local state setters
      if (updates.buildMenuOpen !== undefined)
        setLocalBuildMenuOpen(updates.buildMenuOpen);
      if (updates.selectedCategory !== undefined)
        setLocalSelectedCategory(updates.selectedCategory);
      if (updates.selectedBuilding !== undefined)
        setLocalSelectedBuilding(updates.selectedBuilding);
    }
  };

  const contextValue: UIContextType = {
    // State
    saveMenuOpen,
    debugMenuOpen,
    achievementMenuOpen,
    researchMenuOpen,
    productionMenuOpen,
    helpMenuOpen,
    leftPanelOpen,
    inventoryOpen,
    inventoryPulseId,
    buildMenuOpen,
    prestigeMenuOpen,
    startMenuOpen: effectiveStartMenuOpen,
    buildMode,
    selectedCategory,
    selectedBuilding,
    buildingStatusMenuTarget,

    // Actions
    setSaveMenuOpen,
    setDebugMenuOpen,
    setAchievementMenuOpen,
    setResearchMenuOpen,
    setProductionMenuOpen,
    setHelpMenuOpen,
    setLeftPanelOpen,
    setInventoryOpen: setInventoryOpenState,
    triggerInventoryPulse: () =>
      setInventoryPulseId(previousId => previousId + 1),
    setBuildMenuOpen,
    setPrestigeMenuOpen,
    setStartMenuOpen,
    setSelectedCategory,
    setSelectedBuilding,
    setBuildMenuState,
    setBuildingStatusMenuTarget,
    exitBuildMode,
    openBuildingStatusMenu,
    closeBuildingStatusMenu,
  };

  return (
    <UIContext.Provider value={contextValue}>{children}</UIContext.Provider>
  );
};
